import React, { useState, useRef } from 'react';
import { Upload, Image as ImageIcon, Download } from 'lucide-react';
import Header from "../../components/Header";

export default function ImageInverter() {
  const [originalImage, setOriginalImage] = useState(null);
  const [invertedImage, setInvertedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setOriginalImage(event.target.result);
        processImage(img);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const processImage = (img) => {
    setLoading(true);
    
    setTimeout(() => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions to match image
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw the image on canvas
      ctx.drawImage(img, 0, 0);
      
      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Invert all pixel values and change background from black to white
      for (let i = 0; i < data.length; i += 4) {
        // Invert RGB values
        data[i] = 255 - data[i];         // R
        data[i + 1] = 255 - data[i + 1]; // G
        data[i + 2] = 255 - data[i + 2]; // B
        // Alpha channel remains unchanged
      }
      
      // Put the modified image data back on the canvas
      ctx.putImageData(imageData, 0, 0);
      
      // Get the data URL from canvas
      setInvertedImage(canvas.toDataURL('image/png'));
      setLoading(false);
    }, 50);
  };

  const handleDownload = (imageUrl, filename) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header title="X-Ray Image Inverter" />
      <div className="p-6">
        <div className="mx-auto w-full max-w-4xl bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-4 text-white">
            <p className="text-center text-gray-600">Upload an X-ray image to convert it from negative to positive</p>
          </div>
        
        <div className="p-6">
          {/* File Upload Area */}
          <div 
            onClick={triggerFileInput}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
          >
            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/*" 
              onChange={handleFileChange} 
              className="hidden"
            />
            <Upload className="mx-auto text-gray-400 mb-2" size={48} />
            <p className="text-gray-500 mb-1">Click to upload an X-ray image</p>
            <p className="text-xs text-gray-400">Upload the black background version to convert it</p>
          </div>
          
          {/* Image Display */}
          {originalImage && (
            <div className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Original Image */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 p-2 flex justify-between items-center">
                    <span className="font-medium">Original X-ray (Negative)</span>
                    <button 
                      onClick={() => handleDownload(originalImage, 'original-xray.png')}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                  <div className="p-4 flex justify-center bg-black">
                    <img 
                      src={originalImage} 
                      alt="Original X-ray" 
                      className="max-h-64 max-w-full object-contain"
                    />
                  </div>
                </div>
                
                {/* Inverted Image */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 p-2 flex justify-between items-center">
                    <span className="font-medium">Converted X-ray (Positive)</span>
                    {invertedImage && (
                      <button 
                        onClick={() => handleDownload(invertedImage, 'positive-xray.png')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Download size={16} />
                      </button>
                    )}
                  </div>
                  <div className="p-4 flex justify-center bg-white">
                    {loading ? (
                      <div className="h-64 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      </div>
                    ) : invertedImage ? (
                      <img 
                        src={invertedImage} 
                        alt="Positive X-ray" 
                        className="max-h-64 max-w-full object-contain"
                      />
                    ) : (
                      <div className="h-64 flex items-center justify-center text-gray-400">
                        <p>Processing image...</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {!originalImage && (
            <div className="mt-8 text-center text-gray-500 py-12 bg-gray-50 rounded-lg">
              <ImageIcon className="mx-auto text-gray-300 mb-2" size={48} />
              <p>Upload an X-ray image to convert it from negative to positive</p>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 p-4 border-t">
          <p className="text-sm text-gray-500 text-center">
            This tool converts X-ray images from negative (black background) to positive (white background) format.
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}